import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser'
import { IonActionSheet } from '@ionic/react'
import { car, close, stopwatch } from 'ionicons/icons'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../../../analytics/utils/firebaseUtils'
import { AppContext } from '../../../../contexts/AppContext'
import useUser from '../../../../contexts/hooks/useUser'
import { ModalContext } from '../../../../contexts/ModalContext'
import { trpc } from '../../../../modules/trpc'

interface CarActionSheetProps {
	menuOpen: boolean
	setMenuOpen: (open: boolean) => void
	vehicle: any
	setSmartChargingPolicy: (params: {
		hashedPid: string
		vehicleId: string
		options: {
			isEnabled: boolean
			deadline: string
		}
	}) => void
}

const CarActionSheet = ({
	menuOpen,
	setMenuOpen,
	vehicle,
	setSmartChargingPolicy,
}: CarActionSheetProps) => {
	const utils = trpc.useContext()

	const { closeModal } = useContext(ModalContext)
	const { isNativePlatform } = useContext(AppContext)

	const { t } = useTranslation(['pages_electricCar', 'common'])

	const { user } = useUser()

	const { mutate: unlinkVehicle } = trpc.eNode.user.disconnect.vendorType.useMutation({
		onMutate() {
			logEvent('click-unlink-vehicle')
		},
		onSuccess: () => {
			utils.objects.getEnergyReport.invalidate()
			utils.eNode.vehicles.invalidate()

			closeModal()
		},

		onError: () => {
			console.error('Error delinking vehicle')
		},
	})

	const redirectUri = isNativePlatform
		? 'https://vihreaenergia.fi/'
		: window.location.origin + '/devices'

	const { refetch: refetchRelinkVehicle } = trpc.eNode.user.relink.useQuery(
		{ assetId: vehicle?.id || '', language: 'browser', redirectUri },
		{
			enabled: false,
			onSuccess: data => {
				if (data?.linkUrl) {
					if (isNativePlatform) {
						// Open in InAppBrowser for native platforms
						const browser = InAppBrowser.create(data.linkUrl, '_blank', 'location=yes')

						browser.on('loadstart').subscribe((event: any) => {
							// Check if redirected back to our app
							if (
								event.url.includes('vihreaenergia.fi') ||
								event.url.includes(window.location.origin)
							) {
								browser.close()
								utils.objects.getEnergyReport.invalidate()
								utils.eNode.vehicles.invalidate()
								closeModal()
							}
						})
					} else {
						// Open in new tab/window for web platforms
						window.open(data.linkUrl, '_blank')
					}
				}
			},

			onError: () => {
				console.error('Error relinking vehicle')
			},
		}
	)

	return (
		<IonActionSheet
			isOpen={menuOpen}
			onDidDismiss={() => setMenuOpen(false)}
			buttons={[
				{
					text: vehicle?.smartChargingPolicy.isEnabled
						? t('electricCarDetail.poistaAlylataus')
						: t('electricCarDetail.aktivoiAlylataus'),
					icon: stopwatch,
					handler: () => {
						if (!user?.hashedPid || !vehicle?.id) return
						logEvent('click-ev-set-smart-changing-policy')
						setSmartChargingPolicy({
							hashedPid: user.hashedPid,
							vehicleId: vehicle.id,
							options: {
								isEnabled: !vehicle?.smartChargingPolicy.isEnabled,
								deadline: vehicle?.smartChargingPolicy.deadline ?? '08:00',
							},
						})
					},
				},
				{
					text: t('electricCarDetail.poistaSähköauto'),
					icon: car,
					handler: () => {
						if (!user?.hashedPid || !user.id) return
						logEvent('click-ev-unlink')
						unlinkVehicle({
							userHashedPid: user.hashedPid,
							userId: user.id,
							vendor: vehicle?.vendor ?? '',
							vendorType: 'vehicle',
						})
						closeModal()
					},
				},
				{
					text: t('electricCarDetail.relinkVehicle'),
					icon: car,
					handler: () => {
						logEvent('click-ev-relink')
						if (!vehicle?.id) return
						refetchRelinkVehicle()
					},
				},
				{
					text: t('peruuta', { ns: 'common' }),
					role: 'cancel',
					icon: close,
					handler: () => {
						logEvent('click-generic-cancel')
					},
				},
			]}
		/>
	)
}

export default CarActionSheet
